// Consent banner for usage statistics (TTDSG § 25, DSGVO). Two equal choices, nothing is
// stored or sent before "Statistik erlauben". The choice can be changed at any time via
// openConsentSettings() / <ConsentSettingsLink />.
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CONSENT_EVENT, CONSENT_KEY, readConsent, useAnalyticsTracking, writeConsent, type ConsentLevel,
} from "./analytics";

const OPEN_EVENT = "kk-consent-open";

/** Re-opens the banner so the visitor can change the statistics consent. */
export function openConsentSettings() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Current consent, updated live when it changes (also in other tabs). */
export function useConsent(): ConsentLevel | null {
  const [c, setC] = useState<ConsentLevel | null>(() => readConsent());
  useEffect(() => {
    const upd = () => setC(readConsent());
    const onStorage = (e: StorageEvent) => { if (e.key === CONSENT_KEY) upd(); };
    window.addEventListener(CONSENT_EVENT, upd);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener(CONSENT_EVENT, upd); window.removeEventListener("storage", onStorage); };
  }, []);
  return c;
}

/** A link-styled button, e.g. for the footer or the Datenschutz page. */
export function ConsentSettingsLink({ className }: { className?: string }) {
  return (
    <button type="button" className={className ?? "kk-consent-link"} onClick={openConsentSettings}>
      Statistik-Einwilligung ändern
    </button>
  );
}

const css = `
.kk-consent{position:fixed;z-index:1000;left:50%;bottom:max(.75rem,env(safe-area-inset-bottom));transform:translateX(-50%);
 width:min(52rem,calc(100% - 1.5rem));background:#fffdf6;color:#2b1a0e;border:1px solid #ead9a8;border-radius:.875rem;
 box-shadow:0 .5rem 1.5rem rgba(60,35,10,.16);padding:.625rem .75rem .625rem 1rem;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
 font-size:.875rem;line-height:1.4;display:flex;align-items:center;gap:.75rem;flex-wrap:wrap}
.kk-consent:focus{outline:none}
.kk-consent p{margin:0;flex:1 1 18rem}
.kk-consent a{color:#5c3517;text-decoration:underline;text-underline-offset:2px}
.kk-consent__row{display:flex;gap:.5rem;flex:0 0 auto}
.kk-consent__btn{min-height:2.75rem;padding:.375rem 1rem;border-radius:.625rem;border:2px solid #5c3517;background:#fff;color:#5c3517;font:inherit;font-weight:600;cursor:pointer}
.kk-consent__btn.yes{}
.kk-consent__btn:hover{background:#47280f;border-color:#47280f;color:#fff}
.kk-consent__btn:focus-visible,.kk-consent-link:focus-visible{outline:3px solid #fecf02;outline-offset:2px}
.kk-consent-link{background:none;border:0;padding:.5rem 0;min-height:2.75rem;color:inherit;font:inherit;text-decoration:underline;cursor:pointer}
@media (max-width:639px){.kk-consent{padding:.5rem .625rem;gap:.375rem .5rem}.kk-long{display:none}.kk-consent p{flex-basis:100%}.kk-consent__row{flex:1 1 100%}.kk-consent__btn{flex:1;padding:.25rem .5rem}}
@media print{.kk-consent{display:none}}
`;

export function ConsentBanner({ onlyWhenOpened = false }: { onlyWhenOpened?: boolean }) {
  const consent = useConsent();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const onOpen = () => { setOpen(true); requestAnimationFrame(() => ref.current?.focus()); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // the banner floats over the page: keep room below the content so it never hides the last buttons
  const shown = !pathname.startsWith("/admin") && (open || (consent === null && !onlyWhenOpened));
  useEffect(() => {
    if (!shown) return;
    const el = ref.current; if (!el) return;
    const set = () => { document.body.style.paddingBottom = `${el.offsetHeight + 24}px`; };
    set(); const ro = new ResizeObserver(set); ro.observe(el);
    return () => { ro.disconnect(); document.body.style.paddingBottom = ""; };
  }, [shown]);

  if (pathname.startsWith("/admin")) return null;
  if ((consent !== null || onlyWhenOpened) && !open) return null;

  const choose = (level: ConsentLevel) => { writeConsent(level); setOpen(false); };
  return (
    <section ref={ref} className="kk-consent" role="region" aria-labelledby="kk-consent-title" tabIndex={-1}>
      <style>{css}</style>
      <p id="kk-consent-title">
        <strong>Dürfen wir zählen, welche Seiten Sie wie lange nutzen?</strong> Mit Anmeldung wird das Ihrem Konto zugeordnet. Keine Werbung<span className="kk-long">, keine IP-Adresse, zufällige Kennung im Browser</span>. <Link to="/datenschutz">Details</Link>
        {consent && <> · Aktuell: <strong>{consent === "statistics" ? "Statistik erlaubt" : "nur notwendige"}</strong></>}
      </p>
      <div className="kk-consent__row">
        <button type="button" className="kk-consent__btn" onClick={() => choose("necessary")} aria-label="Nein – nur notwendige">Nur notwendige</button>
        <button type="button" className="kk-consent__btn yes" onClick={() => choose("statistics")} aria-label="Ja – Statistik erlauben">Statistik erlauben</button>
      </div>
    </section>
  );
}

/** Banner + tracker. Mount once inside <BrowserRouter>. */
export function PrivacyLayer() {
  const consent = useConsent();
  useAnalyticsTracking(consent);
  // asked on the public pages; inside the app (and the admin) it would cover buttons – without an answer nothing is counted
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  // in the app it would cover buttons: shown there only when opened via "Statistik-Einwilligung ändern"
  return <ConsentBanner onlyWhenOpened={pathname.startsWith("/app")} />;
}
